import { initializeApp } from 'firebase/app';
import type { Analytics } from 'firebase/analytics';
import { getAnalytics } from 'firebase/analytics';
import { getAuth } from 'firebase/auth';
import { getFirestore as getFirestoreLite } from 'firebase/firestore/lite';
import { getFirestore as getFirestoreFull } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import {
    FIREBASE_STORAGE_BUCKET_PROD,
    FIREBASE_STORAGE_BUCKET_PRODV2,
    FIREBASE_STORAGE_BUCKET_QA,
    FIREBASE_STORAGE_BUCKET_TESTDATA,
    FIRESTORE_DB_PROD_QUESTION_BANK_ID,
    FIRESTORE_DB_PRODV2_QUESTION_BANK_ID,
    FIRESTORE_DB_QUESTION_BANK_ID,
    FIRESTORE_DB_TESTDATA_QUESTION_BANK_ID,
    FIRESTORE_DB_TEST_ID,
    getFirebaseWebConfig,
} from './config';

const firebaseConfig = getFirebaseWebConfig();

export const firebaseApp = initializeApp(firebaseConfig);

/** Firebase Authentication (email/password, SSO, etc. — configure in Firebase Console). */
export const auth = getAuth(firebaseApp);

/** Skipped under Jest to avoid IndexedDB / DOM coupling in node test runs. */
export const firebaseAnalytics: Analytics =
    process.env.NODE_ENV === 'test'
        ? ({} as Analytics)
        : getAnalytics(firebaseApp);

export const db = getFirestoreLite(firebaseApp);

export const db2 = getFirestoreLite(firebaseApp, FIRESTORE_DB_TEST_ID);

/** QA knowledge-base question bank (Firestore database id from env; default `vehiwheelqa`). */
export const dbQuestionBank = getFirestoreLite(firebaseApp, FIRESTORE_DB_QUESTION_BANK_ID);

/** Standalone test bank (`common` + `questions_cl`). */
export const dbQuestionBankTestdata = getFirestoreLite(firebaseApp, FIRESTORE_DB_TESTDATA_QUESTION_BANK_ID);

/**
 * Same database as {@link dbQuestionBank}, non-lite client — required for aggregate counts
 * (`getCountFromServer`). Prefer {@link dbQuestionBank} for normal reads/writes (smaller bundle path).
 */
export const dbQuestionBankFull = getFirestoreFull(firebaseApp, FIRESTORE_DB_QUESTION_BANK_ID);

/** True when {@link FIRESTORE_DB_PROD_QUESTION_BANK_ID} is non-empty. */
export const isQuestionBankProdFirestoreConfigured = Boolean(FIRESTORE_DB_PROD_QUESTION_BANK_ID);

/** True when {@link FIRESTORE_DB_PRODV2_QUESTION_BANK_ID} is non-empty. */
export const isQuestionBankProdv2FirestoreConfigured = Boolean(FIRESTORE_DB_PRODV2_QUESTION_BANK_ID);

/**
 * Production question bank Firestore (named DB). **`null`** when `REACT_APP_FIRESTORE_DB_PROD` is unset.
 */
export const dbQuestionBankProd = FIRESTORE_DB_PROD_QUESTION_BANK_ID
    ? getFirestoreLite(firebaseApp, FIRESTORE_DB_PROD_QUESTION_BANK_ID)
    : null;

/**
 * Same as {@link dbQuestionBankProd} with the full SDK — for `getCountFromServer` on prod if needed.
 */
export const dbQuestionBankProdFull = FIRESTORE_DB_PROD_QUESTION_BANK_ID
    ? getFirestoreFull(firebaseApp, FIRESTORE_DB_PROD_QUESTION_BANK_ID)
    : null;

/**
 * Preprod / prod-prep bank (`vehiwheelprodv2`). **`null`** when `REACT_APP_FIRESTORE_DB_PRODV2` is unset.
 */
export const dbQuestionBankProdv2 = FIRESTORE_DB_PRODV2_QUESTION_BANK_ID
    ? getFirestoreLite(firebaseApp, FIRESTORE_DB_PRODV2_QUESTION_BANK_ID)
    : null;

export const dbQuestionBankProdv2Full = FIRESTORE_DB_PRODV2_QUESTION_BANK_ID
    ? getFirestoreFull(firebaseApp, FIRESTORE_DB_PRODV2_QUESTION_BANK_ID)
    : null;

/** Non-lite client for {@link dbQuestionBankTestdata} (aggregate counts, parity with QA). */
export const dbQuestionBankTestdataFull = getFirestoreFull(
    firebaseApp,
    FIRESTORE_DB_TESTDATA_QUESTION_BANK_ID
);

/** Default project bucket (legacy / Default DB question flows). Same as `storageBucket` in web config. */
export const storage = getStorage(firebaseApp);

/** Which Firestore DB drives `questionImages` metadata — paired Storage bucket for uploads. */
export type QuestionImagesDatabaseId =
    | 'default'
    | 'vehiwheelqa'
    | 'vehiwheelprod'
    | 'vehiwheelprodv2'
    | 'vehiwheeltestdata';

/**
 * Storage bucket id (Console name) paired with the selected question-bank / Test Catalog database.
 * Throws when prod/testdata env vars are missing — same preconditions as {@link getFirebaseStorageForQuestionImages}.
 */
export function getStorageBucketIdForQuestionImages(databaseId: QuestionImagesDatabaseId): string {
    if (databaseId === 'vehiwheelqa') {
        return FIREBASE_STORAGE_BUCKET_QA;
    }
    if (databaseId === 'vehiwheelprod') {
        if (!FIREBASE_STORAGE_BUCKET_PROD) {
            throw new Error(
                'Production question image Storage is not configured (set REACT_APP_FIREBASE_STORAGE_BUCKET_PROD).'
            );
        }
        return FIREBASE_STORAGE_BUCKET_PROD;
    }
    if (databaseId === 'vehiwheelprodv2') {
        if (FIREBASE_STORAGE_BUCKET_PRODV2) return FIREBASE_STORAGE_BUCKET_PRODV2;
        if (FIREBASE_STORAGE_BUCKET_PROD) return FIREBASE_STORAGE_BUCKET_PROD;
        throw new Error(
            'Prod v2 question image Storage is not configured (set REACT_APP_FIREBASE_STORAGE_BUCKET_PRODV2 or reuse PROD).'
        );
    }
    if (databaseId === 'vehiwheeltestdata') {
        if (!FIREBASE_STORAGE_BUCKET_TESTDATA) {
            throw new Error(
                'Test question bank Storage is not configured (set REACT_APP_FIREBASE_STORAGE_BUCKET_TESTDATA).'
            );
        }
        return FIREBASE_STORAGE_BUCKET_TESTDATA;
    }
    return firebaseConfig.storageBucket ?? 'vehiwheel.appspot.com';
}

/**
 * Storage used for `questionImages` uploads and paths: Default DB → default app bucket; QA DB → QA bucket;
 * prod question bank → prod bucket (requires `REACT_APP_FIREBASE_STORAGE_BUCKET_PROD`).
 */
export function getFirebaseStorageForQuestionImages(
    databaseId: QuestionImagesDatabaseId = 'default'
): FirebaseStorage {
    return getStorage(firebaseApp, `gs://${getStorageBucketIdForQuestionImages(databaseId)}`);
}
